import { type Accessor, type JSX, type Setter, createContext, useContext } from "solid-js";
import type { DocumentProviderProps } from ".";
import type { Article, Asset, MoreInfo, Section } from "../types";

export interface DocumentContext {
	buildNumber: Accessor<number>;
	articles: Accessor<Article[]>;
	selected: Accessor<Article | undefined>;
	clickedArticle: Accessor<Article | undefined>;
	keywords: Accessor<string[][]>;
	assets: Accessor<Asset[]>;
	moreInfo: Accessor<MoreInfo[]>;
	searches: Accessor<string[]>;
	documentTitle: Accessor<string>;
	bookmarks: Accessor<Set<string>>;
	selectedMoreInfo: Accessor<MoreInfo | undefined>;
	sections: Accessor<Section[]>;
	tab: Accessor<string>;
	searchAmount: Accessor<number>;

	setSelected: Setter<Article | undefined>;
	setClickedArticle: Setter<Article | undefined>;
	setMoreInfo: Setter<MoreInfo[]>;
	setSearches: Setter<string[]>;
	setDocumentTitle: Setter<string>;
	setTab: Setter<string>;

	// ========================
	// Additional functionality
	// ========================

	handleLinkClick: (event: MessageEvent) => void;
	handleToggleBookmark: () => void;
	handleDirectionArticle: (direction: number) => void;
	closeMoreInfo: () => void;
	isBookmarked: (article: Article | undefined) => boolean;
	isProtected: Accessor<boolean>;

	search: Accessor<string>;
	onSearch: JSX.EventHandlerUnion<HTMLInputElement, InputEvent>;

	documentId: Accessor<string>;

	// ========================
	// Mobile functionality
	// ========================

	sheetOpen: Accessor<boolean>;
	setSheetOpen: Setter<boolean>;
	scrollPosition: Accessor<number>;
	setScrollPosition: Setter<number>;

	// Config related
	"protect-moreinfo"?: DocumentProviderProps["protect-moreinfo"];
}

export const DocumentContext = createContext<DocumentContext>();

export function useDocumentContext() {
	const context = useContext(DocumentContext);
	if (!context) {
		throw new Error("useDocumentContext must be used within a DocumentProvider");
	}
	return context;
}
